import * as React from 'react';
import {
  CategoryData,
  SubCategories,
  Category,
  FacetValuesList,
  CmsPage,
  noop,
  SEOConfig,
  FacetValue,
} from '@packages/common';

import { useFastlane } from '../../hooks/useFastlane';

export interface CategoryContextValue extends CategoryData {
  getCategoryLevel: () => number;
  subCategories: SubCategories;
  isSearchPage: boolean;
  searchTerm: string;
  inactiveFacetValues: FacetValuesList;
  loadMoreFacetValues: (facetSeoName: string, cb?: VoidFunction) => void;
  searchCategory?: Category;
  contentfulConfig: CmsPage | null;
  hasContentAboveGrid: () => boolean;
  seoFacet?: FacetValue;
}

export interface CategoryProviderData {
  categoryInitial: CategoryData;
  subCategories: SubCategories;
  seoFacet?: FacetValue;
}

interface CategoryProviderProps {
  isSearchPage?: boolean;
  searchTerm?: string;
  categoryData?: CategoryProviderData;
  searchCategory?: Category;
  contentfulConfig: CmsPage | null;
}

export const CategoryContext = React.createContext({} as CategoryContextValue);

const initialCategoryData: CategoryProviderProps['categoryData'] = {
  categoryInitial: {
    category: {} as Category,
    facetValues: {},
    facets: [],
    heroFacets: [],
    mainTeaser: {} as any,
    secondaryTeaser: {} as any,
    seoText: {} as SEOConfig,
  },
  subCategories: {
    items: [],
    parents: [],
  },
};

export const CategoryProvider: React.FC<CategoryProviderProps> = ({
  isSearchPage = false,
  searchTerm = '',
  categoryData,
  children,
  searchCategory,
  contentfulConfig,
}) => {
  const fastlaneClient = useFastlane();
  const {
    seoFacet,
    subCategories,
    categoryInitial: { category, facets, facetValues, heroFacets, mainTeaser, secondaryTeaser, seoText },
  } = categoryData || initialCategoryData;

  const activeCategory = isSearchPage && searchCategory ? searchCategory : category;

  const [inactiveFacetValues, setInactiveFacetValues] = React.useState({});

  const loadMoreFacetValues = React.useCallback(
    (facetSeoName: string, cb: VoidFunction = noop) => {
      fastlaneClient.fetchFacetValues(activeCategory.url, facetSeoName).then((data) => {
        setInactiveFacetValues((state) => ({ ...state, [facetSeoName as any]: data }));
        cb();
      });
    },
    [activeCategory.url, fastlaneClient],
  );

  const getCategoryLevel = React.useCallback(() => {
    return category.url.split('/').length - 1;
  }, [category]);

  const hasCmsSlotContent = () =>
    Boolean(
      contentfulConfig?.modules?.above_sorting ||
        contentfulConfig?.modules?.below_title ||
        contentfulConfig?.modules?.above_refinements_row,
    );
  const hasTeaserContent = () => Boolean(mainTeaser?.content || secondaryTeaser?.content);
  const hasContentAboveGrid = () => hasTeaserContent() || hasCmsSlotContent();

  return (
    <CategoryContext.Provider
      value={{
        seoFacet,
        isSearchPage,
        subCategories,
        searchTerm,
        mainTeaser,
        secondaryTeaser,
        seoText,
        category,
        getCategoryLevel,
        facetValues: { ...facetValues, ...inactiveFacetValues },
        facets,
        heroFacets,
        loadMoreFacetValues,
        inactiveFacetValues,
        searchCategory,
        contentfulConfig,
        hasContentAboveGrid,
      }}
    >
      {children}
    </CategoryContext.Provider>
  );
};
